import React, { useEffect, useState } from 'react'
import { deviceDetect } from 'react-device-detect'
import { Layout, Row, Col, Modal, notification } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import SearchRefinement from './components/SearchRefinement'
import NavigationBar from './components/NavigationBar'
import SearchResults from './components/SearchResults'
import DoctorAvailabilityView from './components/DoctorAvailabilityView'
import MobileSearchEngine from './components/Mobile'
import VirtualConsultationTypeSelector from './components/VirtualConsultationTypeSelector'
import VirtualConsultationSearchCriteria from './components/VirtualConsultationSearchCriteria'
import ClinicSearchCriteria from './components/ClinicSearchCriteria'
import { SearchProvider } from './contexts/SearchContext'
import { BookingProvider } from './contexts/BookingContext'
import { useSearchRedux, useBookingRedux } from './hooks/reduxHooks'

const { Content } = Layout

// Main search engine wrapper with providers
const SearchEngineWithProviders = ({
  isVirtualVisit = false,
  isHomeVisit = false,
  preSelectedServiceType = null,
  onBack
}) => {
  // For virtual visits, show consultation type selector first (unless onBack is provided, meaning we're coming from type selector)
  if (isVirtualVisit && !onBack) {
    return (
      <SearchProvider>
        <BookingProvider>
          <VirtualConsultationTypeSelector
            SearchEngineComponent={({ isVirtualVisit, onBack }) =>
              deviceDetect().isMobile ? (
                <MobileSearchEngine isVirtualVisit={isVirtualVisit} isHomeVisit={isHomeVisit} onBack={onBack} />
              ) : (
                <SearchEngine isVirtualVisit={isVirtualVisit} isHomeVisit={isHomeVisit} onBack={onBack} />
              )
            }
          />
        </BookingProvider>
      </SearchProvider>
    )
  }

  // Check if mobile device
  if (deviceDetect().isMobile) {
    return (
      <SearchProvider>
        <BookingProvider>
          <MobileSearchEngine
            isVirtualVisit={isVirtualVisit}
            isHomeVisit={isHomeVisit}
            preSelectedServiceType={preSelectedServiceType}
            onBack={onBack}
          />
        </BookingProvider>
      </SearchProvider>
    )
  }

  // Desktop version
  return (
    <SearchProvider>
      <BookingProvider>
        <SearchEngine
          isVirtualVisit={isVirtualVisit}
          isHomeVisit={isHomeVisit}
          preSelectedServiceType={preSelectedServiceType}
          onBack={onBack}
        />
      </BookingProvider>
    </SearchProvider>
  )
}

// Core search engine component
const SearchEngine = ({ isVirtualVisit = false, isHomeVisit = false, preSelectedServiceType = null, onBack }) => {
  // Redux hooks
  const {
    searchFilters,
    searchResults,
    loading,
    error,
    pagination,
    performSearch,
    changePage,
    updateFilter,
    clearError
  } = useSearchRedux()

  // Local state for new search criteria flow
  // Show search criteria screen for both virtual visits and clinic bookings until user explicitly searches
  const hasRequiredFields = isVirtualVisit
    ? searchFilters.callType && searchFilters.appointmentReason && searchFilters.timeOfDay
    : searchFilters.location && searchFilters.placeId && searchFilters.appointmentReason && searchFilters.timeOfDay // For clinic bookings, require location + reason + time

  const [searchCriteriaAnimating, setSearchCriteriaAnimating] = useState(false)
  const [hasPerformedSearch, setHasPerformedSearch] = useState(false)
  const searchType = searchFilters.searchType || 'general_practitioner'

  // Dynamically determine if we should show search criteria
  // For clinic bookings, ALWAYS show criteria screen until search is performed
  const showNewSearchCriteria = isVirtualVisit ? !hasRequiredFields && !hasPerformedSearch : !hasPerformedSearch // For clinic bookings, show until search is done

  console.log('🔍 Search Engine Flow Debug:', {
    isVirtualVisit,
    isHomeVisit,
    searchFilters,
    hasRequiredFields,
    hasPerformedSearch,
    showNewSearchCriteria,
    'SHOULD SHOW': showNewSearchCriteria ? 'CRITERIA SCREEN' : 'SIDEBAR'
  })

  // Reset search criteria fields when component mounts for clinic bookings ONLY
  useEffect(() => {
    if (!isVirtualVisit && !hasPerformedSearch) {
      console.log('🔄 Forcing clean state for clinic booking - clearing ALL fields')
      // Force clear ALL clinic-specific fields to ensure fresh start
      updateFilter('appointmentReason', '')
      updateFilter('timeOfDay', '')
      updateFilter('address', '')
      updateFilter('placeId', '')
      updateFilter('location', null)
      updateFilter('serviceType', '') // Also clear service type
      console.log('🔄 All clinic fields cleared, should show criteria screen')
    }
  }, []) // Only run once on mount, don't include dependencies that could cause re-runs

  // Pre-fill serviceType if preSelectedServiceType is provided
  useEffect(() => {
    if (preSelectedServiceType && isVirtualVisit) {
      console.log('🎯 Pre-filling serviceType:', preSelectedServiceType)
      updateFilter('serviceType', preSelectedServiceType)
    }
  }, [preSelectedServiceType, isVirtualVisit, updateFilter])
  console.log('Current searchType from Redux:', searchType, 'Full searchFilters:', searchFilters)
  const {
    selectedDoctor,
    selectedSlot,
    showAvailabilityView,
    openAvailabilityView,
    closeAvailabilityView,
    bookingSuccess,
    bookingError,
    clearBookingError
  } = useBookingRedux()

  // Handle search with error handling
  const handleSearch = async (newFilters) => {
    try {
      // For flexi/priority consultations, skip doctor search and go directly to booking
      if (newFilters.isFlexiPriority) {
        console.log('🚀 Desktop - Flexi/Priority detected - skipping search, going to booking')

        // Create a virtual doctor for flexi/priority bookings
        const virtualDoctor = {
          _id: 'flexi-priority-virtual-doctor',
          firstName: 'Available',
          lastName: 'Doctor',
          extraData: {
            profession: 'Medical Practitioner'
          },
          clinicRecord: {
            _id: 'virtual-clinic-flexi-priority',
            name: 'Virtual Consultation',
            address:
              newFilters.serviceType === 'priority'
                ? 'Doctor will call within a few minutes'
                : 'Doctor will contact you during the day',
            // Add virtual consultation cost to trigger payment flow
            virtualConsultationCost: newFilters.serviceType === 'priority' ? 101 : 10 // Demo amounts
          }
        }

        // Open availability view directly with the virtual doctor
        openAvailabilityView(virtualDoctor)
        return
      }

      await performSearch(newFilters)
    } catch (err) {
      notification.error({
        message: 'Search Failed',
        description: err.message || 'Unable to perform search. Please try again.',
        duration: 4
      })
    }
  }

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    updateFilter(filterType, value)
  }

  // Handle new search criteria submission
  const handleNewSearchCriteria = (criteria) => {
    console.log('🔍 handleNewSearchCriteria called with:', criteria)

    // Update all filters at once
    Object.keys(criteria).forEach((key) => {
      updateFilter(key, criteria[key])
    })

    // Mark that we've performed a search
    setHasPerformedSearch(true)

    // Trigger animation and search
    setSearchCriteriaAnimating(true)
    setTimeout(() => {
      handleSearch(criteria)
    }, 500)
  }

  // Handle filter changes for new criteria component
  const handleNewCriteriaFilterChange = (filters) => {
    Object.keys(filters).forEach((key) => {
      updateFilter(key, filters[key])
    })
  }

  // Handle searchType changes
  const setSearchType = (newSearchType) => {
    console.log('Setting searchType in main component:', newSearchType)
    updateFilter('searchType', newSearchType)
  }

  // Handle page changes with error handling
  const handlePageChange = async (page) => {
    try {
      await changePage(page)
    } catch (err) {
      notification.error({
        message: 'Page Navigation Failed',
        description: err.message || 'Unable to load page. Please try again.',
        duration: 4
      })
    }
  }

  // Handle showing availability view
  const handleViewAvailability = (doctor, preSelectedSlot = null) => {
    openAvailabilityView(doctor, preSelectedSlot)
  }

  // Handle closing availability view
  const handleCloseAvailability = () => {
    closeAvailabilityView()
  }

  // Handle appointment booking success
  const handleBookAppointment = (appointmentData) => {
    Modal.success({
      title: 'Appointment Booked Successfully!',
      content: (
        <div>
          <p>
            <strong>Doctor:</strong> {appointmentData.doctor?.firstName} {appointmentData.doctor?.lastName}
          </p>
          <p>
            <strong>Date & Time:</strong> {appointmentData.datetime}
          </p>
          <p>
            <strong>Reason:</strong> {appointmentData.reason}
          </p>
          <p>
            <strong>Clinic:</strong> {appointmentData.clinic?.name}
          </p>
        </div>
      ),
      onOk: () => {
        closeAvailabilityView()
      }
    })
  }

  // Handle errors
  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error',
        description: error,
        duration: 4,
        onClose: clearError
      })
    }
  }, [error, clearError])

  useEffect(() => {
    if (bookingError) {
      notification.error({
        message: 'Booking Error',
        description: bookingError,
        duration: 4,
        onClose: clearBookingError
      })
    }
  }, [bookingError, clearBookingError])

  // Show booking success
  useEffect(() => {
    if (bookingSuccess) {
      notification.success({
        message: 'Booking Confirmed',
        description: 'Your appointment has been successfully booked!',
        duration: 4
      })
    }
  }, [bookingSuccess])

  // Removed auto-switching logic - user must click search button to proceed

  // Initial search on component mount - DISABLED to allow criteria screen to show
  // useEffect(() => {
  //   if (searchResults.length === 0 && !loading && !isVirtualVisit) {
  //     // Use complete searchFilters from Redux state for initial search
  //     performSearch(searchFilters)
  //   }
  // }, []) // Only run on mount

  // Show availability view full screen
  if (showAvailabilityView && selectedDoctor) {
    return (
      <Layout
        style={{
          minHeight: '100vh',
          background: '#f5f5f5'
        }}
      >
        <Content>
          <DoctorAvailabilityView
            doctor={selectedDoctor}
            onClose={handleCloseAvailability}
            onBookAppointment={handleBookAppointment}
            isVirtualVisit={isVirtualVisit}
            isHomeVisit={isHomeVisit}
            searchFilters={searchFilters}
            preSelectedSlot={selectedSlot}
          />
        </Content>
      </Layout>
    )
  }

  // Show search criteria interface when no search has been performed
  if (showNewSearchCriteria) {
    if (isVirtualVisit) {
      return (
        <VirtualConsultationSearchCriteria
          onSearch={handleNewSearchCriteria}
          searchFilters={searchFilters}
          loading={loading}
          onBack={onBack}
          onFilterChange={handleNewCriteriaFilterChange}
          searchResults={searchResults}
          error={error}
          totalResults={pagination.totalResults}
          onViewAvailability={handleViewAvailability}
        />
      )
    } else {
      return (
        <ClinicSearchCriteria
          onSearch={handleNewSearchCriteria}
          searchFilters={searchFilters}
          loading={loading}
          onBack={onBack}
          onFilterChange={handleNewCriteriaFilterChange}
          searchResults={searchResults}
          error={error}
          totalResults={pagination.totalResults}
          onViewAvailability={handleViewAvailability}
        />
      )
    }
  }

  // Show search interface
  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: '#f9fafb'
      }}
    >
      <NavigationBar
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalResults={pagination.totalResults}
        resultsPerPage={pagination.resultsPerPage}
        onPageChange={handlePageChange}
        loading={loading}
        searchType={searchType}
      />

      <Content>
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '20px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col
              xs={24}
              lg={8}
              xl={6}
              style={{
                position: { lg: 'sticky' },
                top: { lg: '20px' },
                height: { lg: 'fit-content' }
              }}
            >
              <SearchRefinement
                searchType={searchType}
                setSearchType={setSearchType}
                onSearch={handleSearch}
                onFilterChange={handleFilterChange}
                searchFilters={searchFilters}
                loading={loading}
                isVirtualVisit={isVirtualVisit}
                isHomeVisit={isHomeVisit}
                preSelectedServiceType={preSelectedServiceType}
                onBack={onBack}
              />
            </Col>

            <Col xs={24} lg={16} xl={18}>
              {/* Show placeholder for virtual visits when no search performed */}
              {isVirtualVisit && searchResults.length === 0 && !loading && !error ? (
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '48px 24px',
                    textAlign: 'center',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ marginBottom: '24px' }}>
                    <SearchOutlined style={{ fontSize: '48px', color: '#9ca3af' }} />
                  </div>
                  <h3
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '12px'
                    }}
                  >
                    Ready to Find Your Doctor
                  </h3>
                  <p
                    style={{
                      fontSize: '16px',
                      color: '#6b7280',
                      marginBottom: '0',
                      lineHeight: '1.5'
                    }}
                  >
                    Complete your preferences on the left and click "Find Doctors" to see available healthcare
                    professionals for your virtual consultation.
                  </p>
                </div>
              ) : (
                <SearchResults
                  searchType={searchType}
                  results={searchResults}
                  loading={loading}
                  onViewAvailability={handleViewAvailability}
                  error={error}
                  totalResults={pagination.totalResults}
                  searchFilters={searchFilters}
                />
              )}
            </Col>
          </Row>
        </div>
      </Content>
    </Layout>
  )
}

export default SearchEngineWithProviders
